import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';
import { faFolderPlus } from '@fortawesome/pro-solid-svg-icons/faFolderPlus';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomMailFolderType } from '@ukpostbox/client-data';
import {
	Badge,
	Button,
	ButtonSize,
	Colour,
	ColourStateSolid,
	ContentBlock,
	ContentLoader,
	DropdownMenu,
	DropdownMenuEntry,
	ComboBoxFilterInput,
	FlexAlign,
	FlexJustify,
	SectionNavigationEntry,
	SectionNavigationEntryContent,
	SectionNavigationEntryWrapper,
	SectionNavigationGroupHeading,
	StackHorizontal,
	StackVertical,
	Tooltip,
	ToolTipPlacement,
	useComboBoxKeyboardNavigation,
	useFilterSelectOptions,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import React, { useRef, useState } from 'react';
// @ts-ignore
import ss from '@ukpostbox/frontend-framework/lib/Components/Navigation/SectionNavigation/SectionNavigation.module.scss';
import { useMailbox } from '../Providers/NewMailboxProvider';
import {
	CustomFolderDragOver,
	CustomFolderEnter,
	CustomFolderLeave,
	DragCustomFolder,
	DropCustomFolder,
} from './SharedFunctions/CustomFolders';

type MailboxFoldersType = {
	mailItemContext?: boolean;
};

type MailFolderEntryType = {
	[key: string]: any;
	data: CustomMailFolderType;
	parentData?: CustomMailFolderType[];
	nested?: boolean;
	showCounts?: boolean;
	mailItemContext?: boolean;
	isFiltered?: boolean;

	draggable: any;
	onDragStart: any;
	onDragEnd: any;
	onDragEnter: any;
	onDragLeave: any;
	onDragOver: any;
};

const dragIcon = document.createElement('img');
dragIcon.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
dragIcon.width = 0;
dragIcon.height = 0;

function CustomFolderEntryContent(props: MailFolderEntryType) {
	const { t } = useTranslation();
	const { UpdateActiveFolderData, ToggleCreateCustomFolderModal, ToggleDeleteCustomFolderModal } = useMailbox();

	const HandleInteraction = (e: any) => {
		e.stopPropagation();
		UpdateActiveFolderData(props.data.id);
	};

	return (
		<SectionNavigationEntryContent
			title={props.data.description}
			addonStart={<Badge colour={props.data.colour} />}
			addonEnd={
				!props.mailItemContext ? (
					<DropdownMenu
						title='Custom Folder Actions'
						trigger={
							<div className={`${ss['section-actions']} folder-actions`}>
								<div
									className={`${ss['section-action-button']} folder-actions-trigger`}
									title={t('customFolders.actions.options')}
									onClick={(e) => HandleInteraction(e)}
									onKeyDown={(e) => (e.code === 'Enter' ? HandleInteraction(e) : undefined)}
									role='button'
									tabIndex={0}
								>
									<FontAwesomeIcon icon={faEllipsisVertical} />
								</div>
							</div>
						}
					>
						<DropdownMenuEntry
							title={t('customFolders.actions.editFolder')}
							onClick={(e) => {
								e.preventDefault();
								ToggleCreateCustomFolderModal(true);
							}}
						/>
						<DropdownMenuEntry
							title={t('customFolders.actions.deleteFolder')}
							onClick={(e) => {
								e.preventDefault();
								ToggleDeleteCustomFolderModal(true);
							}}
						/>
					</DropdownMenu>
				) : undefined
			}
			badge={
				!props.mailItemContext && props.data.unreadCount && props.data.unreadCount > 0 ? (
					<Badge colour={Colour.Alert} value={props.data.unreadCount} />
				) : undefined
			}
		/>
	);
}

function CustomFolderEntry(props: MailFolderEntryType) {
	const { MoveSelectedItems } = useMailbox();

	if (props.mailItemContext) {
		return (
			<SectionNavigationEntryWrapper
				title={props.data.description}
				extraClass={`folder-entry ${props.nested ? 'folder-entry-nested ml-sm' : ''}`}
				callback={() => MoveSelectedItems(props.data.id)}
			>
				<CustomFolderEntryContent {...props} />
			</SectionNavigationEntryWrapper>
		);
	}

	return (
		<SectionNavigationEntryWrapper
			title={props.data.description}
			path={`../mail/custom-folder/${props.data.id}`}
			extraClass={`folder-entry ${props.nested ? 'folder-entry-nested ml-sm' : ''}`}
			callback={() => MoveSelectedItems(props.data.id)}
			data-folder-id={props.data.id}
			extraProps={{
				draggable: props.draggable,
				onDragStart: props.onDragStart,
				onDragEnd: props.onDragEnd,
				onDragEnter: props.onDragEnter,
				onDragLeave: props.onDragLeave,
				onDragOver: props.onDragOver,
			}}
		>
			<CustomFolderEntryContent {...props} />
		</SectionNavigationEntryWrapper>
	);
}

function MailBoxPrimaryFolder(props: MailFolderEntryType) {
	if (!props.isFiltered && props.data.parentFolderId && props.data.parentFolderId !== -1) return null;

	return (
		<>
			<CustomFolderEntry
				data={props.data}
				mailItemContext={props.mailItemContext}
				draggable={props.draggable}
				onDragStart={(e: DragEvent) => props.onDragStart(e)}
				onDragEnd={(e: DragEvent) => props.onDragEnd(e)}
				onDragEnter={(e: DragEvent) => props.onDragEnter(e)}
				onDragLeave={(e: DragEvent) => props.onDragLeave(e)}
				onDragOver={(e: DragEvent) => props.onDragOver(e)}
			/>

			{props.parentData && (
				<>
					{props.parentData.map((folder, index: number) => {
						if (folder.parentFolderId !== props.data.id) return null;

						return (
							<CustomFolderEntry
								key={`folder-${folder.id}${folder.parentFolderId}`}
								data={folder}
								mailItemContext={props.mailItemContext}
								draggable={props.draggable}
								nested
								onDragStart={(e: DragEvent) => props.onDragStart(e)}
								onDragEnd={(e: DragEvent) => props.onDragEnd(e)}
								onDragEnter={(e: DragEvent) => props.onDragEnter(e)}
								onDragLeave={(e: DragEvent) => props.onDragLeave(e)}
								onDragOver={(e: DragEvent) => props.onDragOver(e)}
							/>
						);
					})}
				</>
			)}
		</>
	);
}

function MailboxFolders(props: MailboxFoldersType) {
	const { t } = useTranslation();
	const { isTouchInput } = useLayout();
	const { HandleFolderMove, ToggleCreateCustomFolderModal, customFolderData, customFolderStatus, customFolderError } =
		useMailbox();

	const [folderDragging, setFolderDragging] = useState<any>({ active: false, id: -1 });

	const customFolderContainer = useRef<HTMLDivElement>(null);

	const { filterValue, filteredItems, FilterSelectList, ResetFilterSelectOptions } = useFilterSelectOptions({
		filterData: customFolderData as any,
	});

	const { HandleInputKeys } = useComboBoxKeyboardNavigation({
		itemContainerSelector: customFolderContainer.current,
		itemClassName: ss['section-navigation-entry'],
		skipClassName: 'multi-select-actions',
		resetCallback: ResetFilterSelectOptions,
	});

	const HandleDrop = (e: DragEvent) => {
		const result: any = DropCustomFolder(e);

		if (result.targetId && result.currentId) {
			HandleFolderMove(result);
		}

		setFolderDragging({ active: false, id: -1 });
	};

	return (
		<div className='custom-folder-container'>
			{customFolderStatus === 'pending' ? (
				<ContentLoader />
			) : customFolderStatus === 'error' ? (
				<ContentBlock error title={customFolderError?.name as string} content={customFolderError?.message} />
			) : (
				<>
					{props.mailItemContext && (
						<>
							<div className='p-xs'>
								<ComboBoxFilterInput
									title='Folders Filter'
									filterValue={filterValue}
									hideArrow
									resetCallback={() => {
										ResetFilterSelectOptions();
									}}
									active
									onChange={(e) => FilterSelectList(e.currentTarget.value)}
									onKeyDown={(e) => HandleInputKeys(e)}
									onBlur={(e) => null}
									onFocus={(e) => null}
									onClick={(e) => null}
									clearOnClose
									autoFocusFilter
								/>
							</div>
							<DropdownMenuEntry title={t('divider')} sectionDivider />
							<DropdownMenuEntry
								title={t('customFolders.actions.newFolder')}
								onClick={() => ToggleCreateCustomFolderModal(true)}
								icon={faFolderPlus}
							/>
							<DropdownMenuEntry title={t('divider')} sectionDivider />
						</>
					)}

					{!props.mailItemContext && (
						<StackHorizontal
							align={FlexAlign.Center}
							justify={FlexJustify.Between}
							extraClass='mt-md mb-xs pr-sm'
						>
							<SectionNavigationGroupHeading title={t('customFolders.title')} />
							<Tooltip content={t('customFolders.actions.newFolder')} position={ToolTipPlacement.Right}>
								<Button
									onClick={() => ToggleCreateCustomFolderModal(true)}
									title={t('customFolders.actions.newFolder')}
									squared
									colour={ColourStateSolid.Primary}
									size={ButtonSize.Small}
									icon={faFolderPlus}
									iconOnly
								/>
							</Tooltip>
						</StackHorizontal>
					)}

					<div
						className={`custom-folder-container ${
							folderDragging.active ? 'dragging-active folder-dragging-active' : ''
						}`}
						ref={customFolderContainer}
					>
						{filteredItems &&
							filteredItems.map((folder, index: number) => (
								<MailBoxPrimaryFolder
									key={`customFolder${folder.id}${
										props.mailItemContext ? 'MailItemContext' : 'MenuContext'
									}`}
									data={folder}
									parentData={filteredItems as Array<CustomMailFolderType>}
									draggable={!isTouchInput}
									isFiltered={!!filterValue}
									mailItemContext={props.mailItemContext}
									onDragStart={(e: any) => setFolderDragging(DragCustomFolder(e))}
									onDragEnd={(e: any) => HandleDrop(e)}
									onDragEnter={(e: any) => CustomFolderEnter(e)}
									onDragLeave={(e: any) => CustomFolderLeave(e)}
									onDragOver={(e: any) => CustomFolderDragOver(e)}
								/>
							))}

						{!props.mailItemContext && (
							<SectionNavigationEntry
								title='Reset'
								extraClass='folder-entry folder-reset'
								extraProps={{
									onDragEnd: (e: any) => HandleDrop(e),
									onDragEnter: (e: any) => CustomFolderEnter(e),
									onDragLeave: (e: any) => CustomFolderLeave(e),
									onDragOver: (e: any) => CustomFolderDragOver(e),
								}}
							/>
						)}
					</div>

					{!props.mailItemContext && filteredItems && filteredItems.length === 0 && (
						<ContentBlock
							stacked
							title={t('customFolders.noData.title')}
							content={t('customFolders.noData.description')}
							media={
								<img
									src={`${process.env.PUBLIC_URL}/images/custom-folders.svg`}
									alt={t('customFolders.noData.imageAlt')}
								/>
							}
							addon={
								<Button
									title={t('customFolders.actions.newFolder')}
									onClick={() => ToggleCreateCustomFolderModal(true)}
									colour={ColourStateSolid.Secondary}
								/>
							}
						/>
					)}

					{props.mailItemContext && filteredItems && filteredItems.length === 0 && (
						<StackVertical align={FlexAlign.Center} justify={FlexJustify.Center} extraClass='faded p-md'>
							<FontAwesomeIcon icon={faGear} size='2x' />
							<h6 className='sm-sub-heading mt-sm mb-0'>No Matching Folders</h6>
						</StackVertical>
					)}
				</>
			)}
		</div>
	);
}

export default MailboxFolders;
